import { type IGamificationInfo } from '@/templates/WebCheckoutMercadoPago/@interfaces';
import {
  clearPendingPixGamification,
  readPendingPixGamification,
} from './pendingPixGamification';
import {
  fetchPixPaymentStatus,
  type IPixPaymentStatus,
} from './pixPaymentStatus';
import { applyGamificationPurchase } from './applyGamificationPurchase';

export interface IReconcileResult {
  reconciled: boolean;
  newGamification: IGamificationInfo | null;
}

interface IReconcileArgs {
  currentProfileId: string | number;
  userState: string | undefined;
  gamificationInfo: IGamificationInfo | undefined;
  origin?: string;
}

const EMPTY_RESULT: IReconcileResult = {
  reconciled: false,
  newGamification: null,
};

export async function reconcilePendingPixGamification(
  args: IReconcileArgs
): Promise<IReconcileResult> {
  const pending = readPendingPixGamification();
  if (!pending) return EMPTY_RESULT;

  if (String(pending.profileId) !== String(args.currentProfileId)) {
    return EMPTY_RESULT;
  }

  if (!args.gamificationInfo?.eligible || args.gamificationInfo.cycle_ended) {
    return EMPTY_RESULT;
  }

  let status: IPixPaymentStatus;
  try {
    status = await fetchPixPaymentStatus(pending.transactionId);
  } catch (error: any) {
    if (typeof window !== 'undefined' && window?.newrelic) {
      window.newrelic.noticeError(error, {
        step: 'pix_status_check_error',
        origin: args.origin,
        status: error?.response?.status,
        message: error?.response?.data?.clientMessage || error?.message,
        profileId: args.currentProfileId,
      });
    }
    return EMPTY_RESULT;
  }

  if (!status.paid) return EMPTY_RESULT;

  const newGamification = await applyGamificationPurchase({
    profileId: pending.profileId,
    userState: args.userState || '',
    bundleNameId: pending.bundleNameId,
    transactionId: pending.transactionId,
    origin: args.origin,
  });

  if (!newGamification) return EMPTY_RESULT;

  clearPendingPixGamification();
  return { reconciled: true, newGamification };
}
